import type { ActionPhaseName, SpinResponse } from "@slot/client-engine";
import { fx, spinFixture } from "@slot/game-shell";
import { SCATTER_SYMBOL_ID, SECRET_BONUS_SYMBOL_ID } from "../assets/mergeLadder";
import { GRID_CELL_COUNT } from "../gridSize";
import { BIG_WIN_TIERS, FREE_SPINS_SCATTER_COUNT, placeholderPayHundredths } from "./offlineMath";
import { applyBadges, applyGravity, applyMerges, chooseMergeOutputs, clusterMultiplier, findClusters, maskOf, type Board, type MergeOutput } from "./mergeRules";

/** One spin the offline server plays: the board it reveals and, per column, the symbols that drop in after merges. */
export interface SpinScript {
  /** Rows top to bottom. */
  board: string[][];
  /** Per column, the symbols stacked above the grid, the first one closest to it (it lands lowest). */
  refill: string[][];
}

/** The DSL's event builder, as `spinFixture(...).event` hands it over. */
type FixtureEvent = Parameters<Parameters<ReturnType<typeof spinFixture>["event"]>[2]>[0];
type FixtureBuilder = ReturnType<typeof spinFixture>;

/** What one played spin left: its win and the badges it ends with. */
interface SpinResult {
  winMinor: bigint;
  badges: Map<number, number>;
  /** The scatters on the spin's last board. */
  scatterCells: number[];
  /** The Secret Bonus symbols on the spin's last board. */
  secretBonusCells: number[];
}

/** A free-spins session's snapshot, stamped on every event of a free spin: the spins left after it, the session's total after it. */
interface FeatureSnapshot {
  featureIdentifier: string;
  remaining: number;
  sessionTotalMinor: bigint;
}

/**
 * The offline server: plays scripted spins by the merge rules (`mergeRules.ts`) and writes the round in the backend's
 * shape through the shell's fixture DSL. Each board is evaluated, every cluster pays (its cells' badges added
 * together multiply it), merges, and badges its cells; survivors fall and the refill drops in; the spin ends on a
 * board with no cluster. Badges clear at a base spin's start and persist through a free-spins session.
 *
 * Per winning substep, in order: `grid_win_highlight` per cluster, `merge_symbols` (every output of every cluster),
 * `update_cell_multipliers` (every badge on the board), `update_total_win_meter` (the spin's running win); the next
 * event, `collapse`, carries the board after gravity. A dead board ends the spin: the scatter trigger, the big win,
 * `spin_boundary_end`. A free spin's reveal restates the session's badges after its boundary.
 */
export class MergeRoundBuilder {
  private readonly fixture: FixtureBuilder;
  private readonly betMinor: bigint;
  private sequence = 0;
  private roundWinMinor = 0n;
  private badges = new Map<number, number>();
  private baseSpins = 0;

  public constructor(input: { roundId: string; betMinor: string; balanceMinor: string }) {
    this.fixture = spinFixture(input.roundId).balance("EUR", input.balanceMinor);
    this.betMinor = BigInt(input.betMinor);
  }

  /**
   * A base spin; with `freeSpins` the spin must end on enough scatters, and wins the session (its spin count is the
   * session's length). With `secretBonus` its last board must carry the Secret Bonus symbol on that cell, alone, and the
   * spin offers it (decision 51); the symbol is on no earlier board and never without its offer.
   */
  public baseSpin(
    script: SpinScript,
    options: { freeSpins?: { featureIdentifier: string; spins: readonly SpinScript[] }; secretBonus?: { positionIndex: number; offerIdentifier: string }; mergeChoices?: MergeChoice[] } = {},
  ): this {
    this.baseSpins += 1;
    this.badges = new Map();
    const boundary = `base:${this.baseSpins}`;
    const result = this.playSpin({
      script,
      boundary,
      mode: "base",
      eventNames: { reveal: "reveal", collapse: "collapse" },
      feature: null,
      mergeChoices: options.mergeChoices ?? [],
      onDeadBoard: (e, spin) => {
        const session = options.freeSpins;
        if (session) {
          if (spin.scatterCells.length < FREE_SPINS_SCATTER_COUNT) throw new Error(`baseSpin: ${spin.scatterCells.length} scatters cannot trigger free spins`);
          act(e, fx.presentScatterState({ positionMask: this.mask(spin.scatterCells), scatterCount: String(spin.scatterCells.length), scatterSymbolIdentifier: SCATTER_SYMBOL_ID }));
          act(e, fx.bonusFeatureWon(session.featureIdentifier, String(session.spins.length)));
        } else if (spin.scatterCells.length >= FREE_SPINS_SCATTER_COUNT) {
          throw new Error(`baseSpin: ${spin.scatterCells.length} scatters trigger free spins, but the script has none`);
        }
        const secret = options.secretBonus;
        if (secret) {
          if (spin.secretBonusCells.length !== 1 || spin.secretBonusCells[0] !== secret.positionIndex) {
            throw new Error(`baseSpin: the Secret Bonus must stand alone on cell ${secret.positionIndex} of the last board (it is on ${spin.secretBonusCells.join(",") || "none"})`);
          }
          act(e, presentSecretBonusAction(secret));
        } else if (spin.secretBonusCells.length > 0) {
          throw new Error("baseSpin: a Secret Bonus on the last board needs its offer (`secretBonus`)");
        }
      },
    });
    this.roundWinMinor += result.winMinor;
    if (options.freeSpins) this.playFreeSpins(options.freeSpins);
    return this;
  }

  public build(): SpinResponse {
    return this.fixture.build();
  }

  private playFreeSpins(session: { featureIdentifier: string; spins: readonly SpinScript[] }): void {
    const featureIdentifier = session.featureIdentifier;
    const total = session.spins.length;
    this.fixture.event("feature_enter", this.sequence++, (e) => {
      e.betAmount(this.betMinor.toString()).featureStateSet({ featureIdentifier, featureCountRemaining: String(total), featureAccumulatedWinMinor: "0", featurePhase: "enter" });
      act(e, fx.updateTotalWinMeter("0"));
    });
    // A session starts on a clean board; from there its badges persist from spin to spin.
    this.badges = new Map();
    let sessionTotal = 0n;
    session.spins.forEach((script, i) => {
      // Every event of a spin carries the snapshot after it, so the spin is played first and written with its total.
      const win = this.dryRunWin(script);
      sessionTotal += win;
      const result = this.playSpin({
        script,
        boundary: `fs:${i + 1}`,
        mode: "free_spins",
        eventNames: { reveal: "fs_reveal", collapse: "fs_collapse" },
        feature: { featureIdentifier, remaining: total - (i + 1), sessionTotalMinor: sessionTotal },
        mergeChoices: [],
        onDeadBoard: (_e, spin) => {
          if (spin.scatterCells.length >= FREE_SPINS_SCATTER_COUNT) throw new Error("free spin: the retrigger rule is not settled; keep scatters under the trigger count");
          if (spin.secretBonusCells.length > 0) throw new Error("free spin: the Secret Bonus lands on a base spin's last board only");
        },
      });
      if (result.winMinor !== win) throw new Error("free spin: the dry run and the spin disagree");
    });
    this.roundWinMinor += sessionTotal;
    const roundTotal = this.roundWinMinor.toString();
    this.fixture.event("feature_exit", this.sequence++, (e) => {
      e.betAmount(this.betMinor.toString())
        .accumulatedWinAfter(roundTotal)
        .featureStateSet({ featureIdentifier, featureCountRemaining: "0", featureAccumulatedWinMinor: sessionTotal.toString(), featurePhase: "exit" });
    });
  }

  /** The win a script plays to from the current badges, leaving them as they were. */
  private dryRunWin(script: SpinScript): bigint {
    let board: Board = script.board;
    let badges = new Map(this.badges);
    const refill = script.refill.map((column) => [...column]);
    let win = 0n;
    for (;;) {
      const clusters = findClusters(board);
      if (clusters.length === 0) return win;
      for (const cluster of clusters) win += this.clusterWin(cluster.symbolId, cluster.cells, badges).winMinor;
      const outputs = clusters.flatMap((cluster) => chooseMergeOutputs(board, cluster));
      badges = applyBadges(badges, clusters.flatMap((cluster) => cluster.cells));
      board = applyGravity(applyMerges(board, outputs), refill);
    }
  }

  private playSpin(input: {
    script: SpinScript;
    boundary: string;
    mode: "base" | "free_spins";
    eventNames: { reveal: string; collapse: string };
    feature: FeatureSnapshot | null;
    mergeChoices: MergeChoice[];
    onDeadBoard: (e: FixtureEvent, spin: SpinResult) => void;
  }): SpinResult {
    let board: string[][] = input.script.board;
    const refill = input.script.refill.map((column) => [...column]);
    let spinWin = 0n;
    for (let substep = 0; ; substep++) {
      const eventName = substep === 0 ? input.eventNames.reveal : input.eventNames.collapse;
      const clusters = findClusters(board);
      const current = board;
      const before = this.badges;
      const wins = clusters.map((cluster) => ({ cluster, ...this.clusterWin(cluster.symbolId, cluster.cells, before) }));
      const substepWin = wins.reduce((sum, w) => sum + w.winMinor, 0n);
      spinWin += substepWin;
      const spinWinAfter = spinWin;
      if (clusters.length === 0) {
        const result: SpinResult = { winMinor: spinWin, badges: this.badges, scatterCells: cellsOf(board, SCATTER_SYMBOL_ID), secretBonusCells: cellsOf(board, SECRET_BONUS_SYMBOL_ID) };
        this.fixture.event(eventName, this.sequence++, (e) => {
          this.stamp(e, input.feature, spinWinAfter).gridRows(current);
          if (substep === 0) this.startSpin(e, input.boundary, input.mode, before);
          input.onDeadBoard(e, result);
          const tier = this.bigWinTier(spinWinAfter);
          if (tier) {
            act(e, fx.presentBigWin({ levelIdentifier: tier, spinWinAmount: spinWinAfter.toString(), betAmount: this.betMinor.toString(), winRatioLabel: `${spinWinAfter / this.betMinor}x` }));
          }
          act(e, fx.spinBoundaryEnd(input.boundary, spinWinAfter.toString()));
        });
        return result;
      }
      if (cellsOf(board, SECRET_BONUS_SYMBOL_ID).length > 0) throw new Error(`${input.boundary}: the Secret Bonus lands on a base spin's last board only, not on a board that still wins`);
      const choice = input.mergeChoices[substep];
      const outputs = clusters.flatMap((cluster) => choice?.(current, cluster) ?? chooseMergeOutputs(current, cluster));
      this.badges = applyBadges(before, clusters.flatMap((cluster) => cluster.cells));
      const after = this.badges;
      this.fixture.event(eventName, this.sequence++, (e) => {
        this.stamp(e, input.feature, spinWinAfter).gridRows(current);
        if (substep === 0) this.startSpin(e, input.boundary, input.mode, before);
        for (const w of wins) {
          const positionMask = this.mask(w.cluster.cells);
          const count = String(w.cluster.cells.length);
          const winAmount = w.winMinor.toString();
          act(
            e,
            fx.gridWinHighlight({
              positionMask,
              winAmount,
              symbolIdentifier: w.cluster.symbolId,
              symbolCount: count,
              lines: [{ positionMask, winAmount, symbolCount: count, baseWinAmount: w.baseMinor.toString(), multiplierApplied: String(w.multiplier) }],
            }),
          );
        }
        act(e, mergeSymbolsAction(outputs));
        act(e, updateCellMultipliersAction(after));
        act(e, fx.updateTotalWinMeter(spinWinAfter.toString()));
      });
      board = applyGravity(applyMerges(current, outputs), refill);
    }
  }

  /**
   * A spin's first actions: its boundary and, in a free spin, the badges it starts with (`frame_prepare`, so they show as
   * the board drops in); a base spin starts clean, which the client applies at its boundary.
   */
  private startSpin(e: FixtureEvent, boundary: string, mode: "base" | "free_spins", badges: ReadonlyMap<number, number>): void {
    act(e, fx.spinBoundaryStart(boundary, mode));
    if (mode === "free_spins") act(e, { ...updateCellMultipliersAction(badges), phase: "frame_prepare" });
  }

  /** The event's shared fields: the bet, the running win and, in a free spin, the session's snapshot. */
  private stamp(e: FixtureEvent, feature: FeatureSnapshot | null, runningWinMinor: bigint): FixtureEvent {
    e.reelSet(feature ? "free_spins" : "default")
      .betAmount(this.betMinor.toString())
      // A base spin's events carry the round's running win; a free spin's carry that spin's (the backend's shape).
      .accumulatedWinAfter((feature ? runningWinMinor : this.roundWinMinor + runningWinMinor).toString());
    if (feature) {
      e.featureStateSet({
        featureIdentifier: feature.featureIdentifier,
        featureCountRemaining: String(feature.remaining),
        featureAccumulatedWinMinor: feature.sessionTotalMinor.toString(),
        featurePhase: "update",
      });
    }
    return e;
  }

  private clusterWin(symbolId: string, cells: readonly number[], badges: ReadonlyMap<number, number>): { baseMinor: bigint; multiplier: number; winMinor: bigint } {
    const baseMinor = (this.betMinor * BigInt(placeholderPayHundredths(symbolId, cells.length))) / 100n;
    const multiplier = clusterMultiplier(badges, cells);
    return { baseMinor, multiplier, winMinor: baseMinor * BigInt(multiplier) };
  }

  private bigWinTier(winMinor: bigint): string | null {
    let tier: string | null = null;
    for (const { levelIdentifier, minWinMultiplier } of BIG_WIN_TIERS) if (winMinor >= this.betMinor * BigInt(minWinMultiplier)) tier = levelIdentifier;
    return tier;
  }

  private mask(indices: number[]): string {
    return maskOf(indices, GRID_CELL_COUNT);
  }
}

/** A fixture's own choice of where one substep's outputs land (the server's choice); absent → `chooseMergeOutputs`. */
export type MergeChoice = (board: string[][], cluster: { symbolId: string; cells: number[] }) => MergeOutput[];

/** An action from the DSL or a game-defined one, authored on the event with its phase. */
function act(e: FixtureEvent, a: { type: string; phase: ActionPhaseName; payload: object }): void {
  e.action(a.type, a.payload as Record<string, unknown>, { actionPhaseName: a.phase });
}

/**
 * `merge_symbols` (the merge mechanic's action, decision 47): every output of the substep's clusters. Phase "win" and
 * never skippable: it changes the grid, and the collapse after it drops from the cells it consumed.
 */
function mergeSymbolsAction(outputs: readonly MergeOutput[]) {
  return {
    type: "merge_symbols",
    phase: "win" as const,
    payload: {
      merges: outputs.map((o) => ({ targetPositionIndex: String(o.target), resultSymbolIdentifier: o.resultSymbolId, consumedPositionIndices: o.consumed.map(String) })),
    },
  };
}

/**
 * `present_secret_bonus` (the Secret Bonus feature's action, decision 51): the symbol's cell on the last board and the
 * offer a tap on it opens. Phase "settlement", never skippable: the offer is the spin's outcome.
 */
function presentSecretBonusAction(secret: { positionIndex: number; offerIdentifier: string }) {
  return {
    type: "present_secret_bonus",
    phase: "settlement" as const,
    payload: { positionIndex: String(secret.positionIndex), offerIdentifier: secret.offerIdentifier },
  };
}

/**
 * `update_cell_multipliers` (the cell multiplier feature's action): every badge on the board after the substep's wins,
 * as a mask and the values in index order. Phase "win" and never skippable: the badges are the board's state.
 */
function updateCellMultipliersAction(badges: ReadonlyMap<number, number>) {
  const indices = [...badges.keys()].sort((a, b) => a - b);
  return {
    type: "update_cell_multipliers",
    phase: "win" as const,
    payload: { positionMask: maskOf(indices, GRID_CELL_COUNT), values: indices.map((i) => String(badges.get(i))) },
  };
}

function cellsOf(board: string[][], symbolId: string): number[] {
  const columns = board[0].length;
  const out: number[] = [];
  board.forEach((row, r) => row.forEach((id, c) => id === symbolId && out.push(r * columns + c)));
  return out;
}
